/**
 * @typedef {Object} CustomerBuysCollection
 * @property {string} handle
 * @property {string} id
 * @property {number} productCount
 * @property {string} title
 */

/**
 * @typedef {Object} CustomerBuysProductVariant
 * @property {string} id
 * @property {string} price
 */

/**
 * @typedef {Object} CustomerBuysProduct
 * @property {string} handle
 * @property {string} id
 * @property {string} title
 * @property {CustomerBuysProductVariant[]} variants
 * @property {number} variantCount
 */

/**
 * @typedef {Object} CustomerBuysConfig
 * @property {string} amount
 * @property {CustomerBuysCollection[]} collections
 * @property {'MinimumPurchase' | 'MinimumQuantity'} discountType
 * @property {'Collection' | 'Product'} itemType
 * @property {CustomerBuysProduct[]} products
 * @property {string} quantity
 * @property {string} query
 */

class GiftWithPurchase {
  constructor () {
    /**
     * @property {boolean}
     * @public
     */
    this.applyDiscountCodeAutomatically =
      this.getApplyDiscountCodeAutomatically()

    /**
     * @property {string}
     * @public
     */

    this.discountCode = this.shouldRun ? this.getDiscountCode() : null

    this.onPushCartChange = this.onPushCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RERENDER,
      this.onPushCartChange.bind(this),
    )
  }

  async onPushCartChange (event) {
    if (!this.shouldRun) return

    /**
     * @type {ShopifyCart | null}
     */
    const state =
      event.detail.state != null && 'discount_codes' in event.detail.state
        ? event.detail.state
        : await window.BAO.Utils.fetchJSON(`/cart.js`)

    const cartIsValid = this.getCartIsValid(state)

    if (!cartIsValid) {
      await this.removeGwpItems(state)
      this._gwpDeclined = false
      this._hadGwp = false
    }

    if (cartIsValid && this.data?.autoAddVariantId) {
      const hasGwp = state.items.some((item) => item.properties?._gwp)

      if (
        !hasGwp &&
        !this._gwpDeclined &&
        !this._autoAddInflight &&
        this.data.autoAddVariantId
      ) {
        this._autoAddInflight = true
        await this.addDiscountCode(state)
        await window.BAO.Cart.addItem([
          {
            id: this.data.autoAddVariantId,
            quantity: 1,
            properties: {
              _gwp: true,
            },
          },
        ])

        this._autoAddInflight = false
      }
      return
    }

    if (!this.applyDiscountCodeAutomatically) return

    const alreadyHasDiscountCode = this.getAlreadyHasDiscountCode(state)

    if (alreadyHasDiscountCode && !cartIsValid) {
      return await this.removeDiscountCode(state)
    } else if (cartIsValid && !alreadyHasDiscountCode) {
      return await this.addDiscountCode(state)
    }
  }

  /**
   * @param {ShopifyCart} state
   */
  async addDiscountCode (state) {
    return await window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
        this.discountCode,
      ].join(','),
    )
  }

  /**
   * @param {ShopifyCart} state
   */
  async removeDiscountCode (state) {
    return await window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
      ].join(','),
    )
  }

  /**
   * @param {ShopifyCart} state
   */
  async removeGwpItems (state) {
    const keys = state.items
      .filter((item) => item.properties?._gwp)
      .map((item) => item.key)

    if (keys.length === 0) return

    await window.BAO.Cart.removeItems(keys)
  }

  /**
   * @returns {string}
   */
  getDiscountCode () {
    return this.data.discountCode
  }

  /**
   * @param {ShopifyCart} cart
   *
   * @returns {boolean}
   */
  getAlreadyHasDiscountCode (cart) {
    return cart.discount_codes.some(
      (discountCode) => discountCode.code === this.discountCode,
    )
  }

  /**
   * @returns {boolean}
   */
  getApplyDiscountCodeAutomatically () {
    if (!this.shouldRun) return false

    return this.data.applyCodeAutomatically
  }

  /**
   * @returns {boolean}
   */
  getCartIsValid (state) {
    const {
      discountType,
      itemType,
      thresholdAmount,
      thresholdQuantity,
      qualifyingProductIds,
    } = this.data

    if (itemType === 'Collection' || !qualifyingProductIds?.length) {
      return this.data.passedThreshold
    }

    const qualifyingItems = state.items.filter(
      (item) =>
        !item.properties?._gwp &&
        qualifyingProductIds.includes(item.product_id),
    )

    if (discountType === 'MinimumPurchase') {
      return (
        qualifyingItems.reduce((sum, item) => sum + item.final_line_price, 0) >=
        thresholdAmount
      )
    }

    if (discountType === 'MinimumQuantity') {
      return (
        qualifyingItems.reduce((sum, item) => sum + item.quantity, 0) >=
        thresholdQuantity
      )
    }

    return this.data.passedThreshold
  }

  /**
   * @param {ShopifyCart} cart
   *
   * @returns {string[]}
   */
  getCurrentAppliedDiscountCodes (cart) {
    return cart.discount_codes.map((discountCode) => discountCode.code)
  }

  get dataElement () {
    return document.querySelector(`script[data-gift-with-purchase-data]`)
  }

  get shouldRun () {
    return !!this.dataElement
  }

  /**
   * @returns {{
   *   applyCodeAutomatically: boolean,
   *   discountCode: string,
   *   passedThreshold: boolean
   * }}
   */
  get data () {
    return JSON.parse(this.dataElement.textContent.trim())
  }

  get _gwpDeclinedKey () {
    return `gwp-declined-${this.data?.autoAddVariantId}`
  }

  get _gwpDeclined () {
    return sessionStorage.getItem(this._gwpDeclinedKey) === '1'
  }

  set _gwpDeclined (val) {
    if (val) {
      sessionStorage.setItem(this._gwpDeclinedKey, '1')
    } else {
      sessionStorage.removeItem(this._gwpDeclinedKey)
    }
  }
}

window.BAO.GiftWithPurchase = new GiftWithPurchase()

class GiftWithPurchaseItem extends window.BAO.CustomElement() {
  static requiredAttributes () {
    return ['data-product-handle']
  }

  static requiredElements () {
    return ['form', 'idInput', 'product-option']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'change',
      this.onFormChange.bind(this),
    )
  }

  async onFormChange (e) {
    const chosenOption = Array.from(e.target.options).find(
      (option) => option.value === e.target.value,
    )
    if (!chosenOption) {
      console.error(`Unable to find option for ${e.target.value}`)

      return
    }

    this.setIdInputValue(chosenOption.getAttribute('data-variant-id'))

    if (this.allValuesAreFilled) {
      return this.els.form.element.requestSubmit()
    }

    const productOptionValues = this.els['product-option'].elements.map(
      (option) => {
        if (option.value === '') {
          return option.getAttribute('data-default-option-value-id')
        }

        const selectedOption = Array.from(option.options).find(
          (o) => o.value === option.value,
        )

        return selectedOption.getAttribute('data-option-id')
      },
    )

    const response = await window.BAO.sectionRenderer.fetchSingle(
      'product-gift-with-purchase-unclaimed',
      `/products/${this.getAttribute('data-product-handle')}?option_values=${productOptionValues.join(',')}`,
    )
    const newSection = new DOMParser().parseFromString(response, 'text/html')

    const newProductOptions = Array.from(
      newSection.querySelectorAll(this.els['product-option'].selector),
    )

    for (const newProductOption of newProductOptions) {
      const currentProductOption = this.els['product-option'].elements.find(
        (option) =>
          option.getAttribute('name') === newProductOption.getAttribute('name'),
      )
      if (!currentProductOption) continue

      for (let i = 0; i < currentProductOption.options.length; i++) {
        const currentOption = currentProductOption.options[i]
        const newOption = newProductOption.options[i]

        if (currentOption.value === '') continue

        currentOption.setAttribute(
          'data-variant-id',
          newOption.getAttribute('data-variant-id'),
        )

        if (!currentOption.hasAttribute('data-perma-disabled')) {
          currentOption.disabled = newOption.disabled
        }
      }
    }
  }

  /**
   * @param {string} value
   */
  setIdInputValue (value) {
    this.els.idInput.element.value = value
  }

  get allValuesAreFilled () {
    return this.els['product-option'].elements.every((option) => {
      return option.value !== ''
    })
  }
}

if (!customElements.get('gift-with-purchase-item')) {
  customElements.define('gift-with-purchase-item', GiftWithPurchaseItem)
}

class GiftWithPurchasePDP extends HTMLElement {
  connectedCallback () {
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChange.bind(this),
    )
  }

  async onCartChange () {
    const sectionId = this.closest('product-info')?.dataset.sectionId
    if (!sectionId) return

    const response = await window.BAO.sectionRenderer.fetchSingle(
      sectionId,
      window.location.pathname,
    )

    const newDOM = window.BAO.sectionRenderer.convertToDOM(response)
    const newContent = newDOM.querySelector('gift-with-purchase-pdp')

    if (newContent) {
      this.innerHTML = newContent.innerHTML
    }
  }
}

if (!customElements.get('gift-with-purchase-pdp')) {
  customElements.define('gift-with-purchase-pdp', GiftWithPurchasePDP)
}
